import { useCallback, useEffect } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

export interface ActiveTeamSearch {
  /** Index of the selected team tab (0 when the URL names none). */
  activeTeamIndex: number;
  /** Deliverable to scroll to and highlight, consumed once. */
  focusDeliverableId: string | undefined;
  setActiveTeamIndex: (index: number) => void;
}

/**
 * The config page's URL-backed view state: which team tab is active, and which
 * deliverable to focus after a jump from the results timeline.
 *
 * Both writes change only the search params of the page already on screen, so
 * they pass `resetScroll: false` — the router otherwise scrolls the window to
 * the top on every navigation, which threw the user back to the Features list
 * on each team switch.
 */
export function useActiveTeamSearch(): ActiveTeamSearch {
  const search = useSearch({ from: '/simulation/$simulationId' });
  const navigate = useNavigate();

  const activeTeamIndex = search.activeTeam ?? 0;
  const focusDeliverableId = search.focus;

  const setActiveTeamIndex = useCallback(
    (index: number) => {
      navigate({
        to: '.',
        search: (prev) => ({ ...prev, activeTeam: index === 0 ? undefined : index }),
        replace: true,
        resetScroll: false,
      });
    },
    [navigate],
  );

  // Clear focus from URL after it's been consumed (prevents re-triggering on navigation)
  useEffect(() => {
    if (focusDeliverableId) {
      navigate({
        to: '.',
        search: (prev) => ({ ...prev, focus: undefined }),
        replace: true,
        resetScroll: false,
      });
    }
  }, [focusDeliverableId, navigate]);

  return { activeTeamIndex, focusDeliverableId, setActiveTeamIndex };
}
